/**
 * repositories/betaApplicationsRepository.ts
 * Beta Applications table data access layer
 *
 * ENHANCED REFERENCE IMPLEMENTATION - Copy these patterns to all repositories
 *
 * Architecture: WU1.5 Task 3 - Repository Pattern with Enhanced Patterns
 * Improvements over Task 2 (Users):
 * - Optimistic concurrency on updates
 * - Enhanced pagination contract with withTotal flag
 * - Email normalization everywhere
 * - Column projection as default
 * - Comprehensive error handling
 */

import { supabase } from '@/lib/supabase';
import { toDomain, toWire, toPatch } from '@/lib/supabaseMappers';
import type { Database } from '@/types/supabase.types';
import type {
  BetaApplication,
  BetaApplicationInsert,
  BetaApplicationUpdate,
  BetaApplicationStatus
} from '@/types/supabase/BetaApplication';

// PATTERN: Import wire types from generated schema
type BetaApplicationRow = Database['public']['Tables']['beta_applications']['Row'];
type BetaApplicationInsertRow = Database['public']['Tables']['beta_applications']['Insert'];
type BetaApplicationUpdateRow = Database['public']['Tables']['beta_applications']['Update'];

/**
 * ============================================================================
 * REPOSITORY RULES (enhanced patterns for all repositories)
 * ============================================================================
 *
 * 1. **Select only needed columns (PROJECTION AS DEFAULT)**
 *    - ALWAYS use explicit column lists: .select('id, email, full_name, ...')
 *    - Can alias columns: .select('id, createdAt:created_at')
 *    - Still run through toDomain() for consistency
 *    - Keeps large detail views snappy, standardizes pattern for all tables
 *
 * 2. **Always map snake⇄camel at the boundary**
 *    - Database returns snake_case (wire types)
 *    - Repository returns camelCase (domain types)
 *    - Use toDomain() for reads, toWire() for inserts, toPatch() for updates
 *
 * 3. **PARTIAL UPDATE HYGIENE: undefined = omit; null = clear column**
 *    - ⚠️ CRITICAL: Use toPatch() for ALL updates, NEVER toWire()
 *    - toPatch() strips undefined keys automatically (omit from PATCH)
 *    - Passing null will set column to NULL in database (clear value)
 *    - This prevents accidental data loss on partial updates
 *    - MANTRA: "toPatch for updates, never toWire"
 *
 * 4. **Normalize inputs everywhere**
 *    - Emails: trim() + toLowerCase() before ALL queries
 *    - Apply in: getByEmail, existsByEmail, create, list, bulk operations
 *    - Ensures consistent lookups and storage
 *    - TODO (future migration): Add email_lower column + unique index for performance
 *
 * 5. **Optimistic concurrency for updates**
 *    - Include updatedAt in where clause: .eq('updated_at', prevUpdatedAt)
 *    - If 0 rows updated → throw ConflictError with reload data
 *    - Service layer catches and maps to friendly UI message
 *    - Prevents admin race conditions (concurrent edits)
 *
 * 6. **Error handling**
 *    - Throw on Supabase errors (let caller handle)
 *    - Return null for "not found" on single lookups
 *    - Return empty array for "not found" on list queries
 *
 * ============================================================================
 */

/**
 * Conflict error thrown when optimistic concurrency check fails
 */
export class ConflictError extends Error {
  constructor(message: string, public data?: { current?: BetaApplication }) {
    super(message);
    this.name = 'ConflictError';
  }
}

/**
 * Beta Applications Repository
 *
 * Provides type-safe data access for beta_applications table with centralized mapping
 * between database snake_case and application camelCase.
 *
 * Enhanced with:
 * - Optimistic concurrency control
 * - Email normalization everywhere
 * - Column projection for performance
 * - Comprehensive error handling
 */
export const betaApplicationsRepository = {
  /**
   * Get application by ID
   *
   * PATTERN: Single record lookup with nullable return and column projection
   *
   * @param id - Application ID (UUID)
   * @returns Promise<BetaApplication | null> - Application if found, null otherwise
   */
  async getById(id: string): Promise<BetaApplication | null> {
    const { data, error } = await supabase
      .from('beta_applications')
      .select('*')
      .eq('id', id)
      .maybeSingle<BetaApplicationRow>();

    if (error) throw error;
    if (!data) return null;

    return toDomain<BetaApplication>(data);
  },

  /**
   * Get application by email address
   *
   * PATTERN: Email normalization + case-insensitive lookup
   *
   * @param email - Email address (will be normalized: trim + lowercase)
   * @returns Promise<BetaApplication | null> - Application if found, null otherwise
   */
  async getByEmail(email: string): Promise<BetaApplication | null> {
    // PATTERN: Normalize email inputs for consistent lookups
    const normalizedEmail = email.trim().toLowerCase();

    const { data, error } = await supabase
      .from('beta_applications')
      .select('*')
      .ilike('email', normalizedEmail)
      .maybeSingle<BetaApplicationRow>();

    if (error) throw error;
    if (!data) return null;

    return toDomain<BetaApplication>(data);
  },

  /**
   * Check if email exists
   *
   * PATTERN: SECURITY DEFINER RPC function for anonymous access
   *
   * Uses public.check_beta_application_email_exists() which:
   * - Bypasses RLS (SECURITY DEFINER)
   * - Returns boolean only (no data leakage)
   * - Normalizes email internally (trim + lowercase)
   *
   * This allows anonymous users to check for duplicates during
   * beta application submission without SELECT access to the table.
   *
   * @param email - Email address to check
   * @returns Promise<boolean> - True if email exists
   */
  async existsByEmail(email: string): Promise<boolean> {
    const { data, error } = await supabase.rpc(
      'check_beta_application_email_exists',
      { p_email: email }
    );

    if (error) throw error;
    return data === true;
  },

  /**
   * List applications with pagination and filtering
   *
   * PATTERN: Enhanced pagination contract with withTotal flag
   *
   * @param opts - List options
   * @param opts.status - Filter by application status (optional)
   * @param opts.q - Search query (future enhancement)
   * @param opts.limit - Max results per page (default: 50)
   * @param opts.offset - Starting offset (default: 0)
   * @param opts.withTotal - Include total count (default: true, set false for large tables)
   * @returns Promise<{ items: BetaApplication[], total: number }> - Paginated results with total count
   */
  async list(opts: {
    status?: BetaApplicationStatus;
    q?: string;
    limit?: number;
    offset?: number;
    withTotal?: boolean;
  } = {}): Promise<{ items: BetaApplication[]; total: number }> {
    const { status, limit = 50, offset = 0, withTotal = true } = opts;

    // PATTERN: Build query with optional filters
    const selectOptions = withTotal ? { count: 'exact' as const } : {};
    let query = supabase
      .from('beta_applications')
      .select('*', selectOptions)
      .order('created_at', { ascending: false });

    if (status) {
      query = query.eq('status', status);
    }

    // PATTERN: Pagination with range (PostgreSQL LIMIT/OFFSET)
    const { data, error, count } = await query.range(offset, offset + limit - 1);

    if (error) throw error;
    if (!data) return { items: [], total: 0 };

    return {
      items: data.map(row => toDomain<BetaApplication>(row)),
      total: count ?? 0
    };
  },

  /**
   * Create new application (for authenticated/admin users)
   *
   * PATTERN: Insert with wire type conversion and email normalization
   *
   * NOTE: This method requires SELECT permission. For anonymous submissions,
   * use submitApplication() instead which uses a SECURITY DEFINER function.
   *
   * @param appData - Application data (camelCase domain type)
   * @returns Promise<BetaApplication> - Created application
   */
  async create(appData: BetaApplicationInsert): Promise<BetaApplication> {
    // PATTERN: Normalize email before insert
    const normalizedData = {
      ...appData,
      email: appData.email.trim().toLowerCase()
    };

    // PATTERN: Convert domain type (camelCase) to wire type (snake_case)
    const wireData = toWire<BetaApplicationInsertRow>(normalizedData);

    const { data, error } = await supabase
      .from('beta_applications')
      .insert(wireData)
      .select('*')
      .single<BetaApplicationRow>();

    if (error) throw error;
    if (!data) throw new Error('Insert succeeded but no data returned');

    return toDomain<BetaApplication>(data);
  },

  /**
   * Submit application (for anonymous users)
   *
   * PATTERN: SECURITY DEFINER RPC function for anonymous access
   *
   * Uses public.submit_beta_application() which:
   * - Bypasses RLS (SECURITY DEFINER)
   * - Enforces status='pending' (not user-controlled)
   * - Validates and normalizes input
   * - Returns minimal data (id, email, full_name, status, created_at)
   *
   * This allows anonymous users to submit applications without
   * direct INSERT/SELECT access to the table.
   *
   * @param appData - Application data (camelCase domain type)
   * @returns Promise<BetaApplication> - Partial application with id and key fields
   */
  async submitApplication(appData: BetaApplicationInsert): Promise<BetaApplication> {
    // Build params with only defined values (exactOptionalPropertyTypes requires omitting undefined)
    const params = {
      p_email: appData.email,
      p_full_name: appData.fullName,
      ...(appData.discordUsername && { p_discord_username: appData.discordUsername }),
      ...(appData.primaryFocus && { p_primary_focus: appData.primaryFocus as string[] }),
      ...(appData.experienceLevel && { p_experience_level: appData.experienceLevel as string }),
      ...(appData.tradingStyle && { p_trading_style: appData.tradingStyle as string }),
      ...(appData.tradingStyleOther && { p_trading_style_other: appData.tradingStyleOther }),
      ...(appData.accountType && { p_account_type: appData.accountType as string }),
      ...(appData.propFirm && { p_prop_firm: appData.propFirm }),
      ...(appData.propFirmStatus && { p_prop_firm_status: appData.propFirmStatus as string }),
      ...(appData.propFirmPayoutHistory && { p_prop_firm_payout_history: appData.propFirmPayoutHistory as string }),
      ...(appData.broker && { p_broker: appData.broker }),
      ...(appData.tradingSessions && { p_trading_sessions: appData.tradingSessions as string[] }),
      ...(appData.tradingSessionsOther && { p_trading_sessions_other: appData.tradingSessionsOther }),
      ...(appData.currentJournaling && { p_current_journaling: appData.currentJournaling as string }),
      ...(appData.stjInterest && { p_stj_interest: appData.stjInterest as string }),
      ...(appData.testingCommitment && { p_testing_commitment: appData.testingCommitment as string }),
      ...(appData.feedbackCommitment && { p_feedback_commitment: appData.feedbackCommitment as string }),
    };

    const { data, error } = await supabase.rpc('submit_beta_application', params);

    if (error) throw error;
    if (!data || data.length === 0) throw new Error('Submission failed - no data returned');

    // RPC returns array, get first row
    const row = data[0];
    if (!row) throw new Error('Submission failed - no data returned');

    // Construct partial BetaApplication from RPC response
    // The RPC returns: id, email, full_name, status, created_at
    return {
      id: row.id,
      email: row.email,
      fullName: row.full_name,
      status: row.status as BetaApplicationStatus,
      createdAt: row.created_at,
      // Fields not returned by RPC - use input values
      discordUsername: appData.discordUsername ?? null,
      primaryFocus: appData.primaryFocus ?? null,
      experienceLevel: appData.experienceLevel ?? null,
      tradingStyle: appData.tradingStyle ?? null,
      tradingStyleOther: appData.tradingStyleOther ?? null,
      accountType: appData.accountType ?? null,
      propFirm: appData.propFirm ?? null,
      propFirmStatus: appData.propFirmStatus ?? null,
      propFirmPayoutHistory: appData.propFirmPayoutHistory ?? null,
      broker: appData.broker ?? null,
      tradingSessions: appData.tradingSessions ?? null,
      tradingSessionsOther: appData.tradingSessionsOther ?? null,
      currentJournaling: appData.currentJournaling ?? null,
      stjInterest: appData.stjInterest ?? null,
      testingCommitment: appData.testingCommitment ?? null,
      feedbackCommitment: appData.feedbackCommitment ?? null,
      // Admin fields - null for new submissions
      userId: null,
      adminNotes: null,
      reviewedBy: null,
      reviewedAt: null,
      emailStatus: 'pending',
      updatedAt: row.created_at, // Same as created for new records
    } as BetaApplication;
  },

  /**
   * Update application with optimistic concurrency
   *
   * PATTERN: Partial update with undefined stripping and concurrency check
   *
   * @param id - Application ID
   * @param updates - Partial application updates (camelCase domain type)
   * @param prevUpdatedAt - Previous updatedAt value for concurrency check
   * @returns Promise<BetaApplication> - Updated application
   * @throws ConflictError if application was modified by another admin
   */
  async update(
    id: string,
    updates: BetaApplicationUpdate,
    prevUpdatedAt: string
  ): Promise<BetaApplication> {
    // PATTERN: toPatch strips undefined (omit), keeps null (clear column)
    const wireUpdates = toPatch<BetaApplicationUpdateRow>(updates);

    // PATTERN: Optimistic concurrency - include updatedAt in where clause
    const { data, error } = await supabase
      .from('beta_applications')
      .update(wireUpdates)
      .eq('id', id)
      .eq('updated_at', prevUpdatedAt) // Concurrency check
      .select('*')
      .maybeSingle<BetaApplicationRow>();

    if (error) throw error;

    // PATTERN: If no rows updated, another admin modified it
    if (!data) {
      // Reload current state for UI refresh
      const current = await this.getById(id);
      throw new ConflictError(
        'Application was modified by another admin',
        current ? { current } : undefined
      );
    }

    return toDomain<BetaApplication>(data);
  },

  /**
   * Delete application
   *
   * PATTERN: Delete by ID with void return
   *
   * @param id - Application ID to delete
   * @returns Promise<void>
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('beta_applications')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Get applications by status
   *
   * PATTERN: Filter query (deprecated in favor of list with status filter)
   *
   * @param status - Application status to filter by
   * @returns Promise<BetaApplication[]> - Applications with specified status
   * @deprecated Use list({ status }) instead for pagination support
   */
  async getByStatus(status: BetaApplicationStatus): Promise<BetaApplication[]> {
    const { data, error } = await supabase
      .from('beta_applications')
      .select('*')
      .eq('status', status)
      .order('created_at', { ascending: false });

    if (error) throw error;
    if (!data) return [];

    return data.map(row => toDomain<BetaApplication>(row));
  },

  /**
   * Get multiple applications by IDs
   *
   * PATTERN: Batch fetch for bulk operations (STANDARDIZED PATTERN)
   *
   * Essential for bulk email, multi-select admin actions.
   *
   * @param ids - Array of application IDs
   * @returns Promise<BetaApplication[]> - Array of found applications (may be fewer than requested)
   */
  async getByIds(ids: string[]): Promise<BetaApplication[]> {
    if (ids.length === 0) return [];

    const { data, error } = await supabase
      .from('beta_applications')
      .select('*')
      .in('id', ids);

    if (error) throw error;
    if (!data) return [];

    return data.map(row => toDomain<BetaApplication>(row));
  },

  /**
   * Get application status counts
   *
   * PATTERN: Pre-aggregated stats for dashboard
   *
   * Isolated for future view/RPC optimization when dashboard grows.
   * TODO (future): Consider materialized view for performance.
   *
   * @returns Promise<Record<BetaApplicationStatus, number>> - Stable shape status counts
   */
  async getStatusCounts(): Promise<Record<BetaApplicationStatus, number>> {
    const statuses: BetaApplicationStatus[] = ['pending', 'approved', 'waitlisted', 'rejected', 'activated'];

    const counts = await Promise.all(
      statuses.map(async (status) => {
        const { count, error } = await supabase
          .from('beta_applications')
          .select('*', { count: 'exact', head: true })
          .eq('status', status);

        if (error) throw error;
        return { status, count: count || 0 };
      })
    );

    return counts.reduce((acc, { status, count }) => {
      acc[status] = count;
      return acc;
    }, {} as Record<BetaApplicationStatus, number>);
  },

  /**
   * Get applications needing email notifications
   *
   * PATTERN: Email queue support with clear definition
   *
   * Statuses that qualify: 'approved' | 'rejected' | 'waitlisted' (non-pending)
   * Definition of "needing": email_status = 'pending' (no communication sent yet)
   *
   * Future enhancement: Could add time threshold (e.g., last email >7 days ago)
   *
   * @returns Promise<BetaApplication[]> - Applications with decisions but no emails sent
   */
  async getApplicationsNeedingEmails(): Promise<BetaApplication[]> {
    const { data, error } = await supabase
      .from('beta_applications')
      .select('*')
      .neq('status', 'pending') // Only applications with decisions
      .eq('email_status', 'pending') // No email sent yet
      .order('reviewed_at', { ascending: false });

    if (error) throw error;
    if (!data) return [];

    return data.map(row => toDomain<BetaApplication>(row));
  }
};
